import { QueryKey, UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';

import type { NotificationResponse } from '@/api/data-contracts';
import { queryKeys } from '@/react-query/constants';
import { getBackendErrorMessage } from '@/shared/lib/errors';

import useNotificationsService from './notifications.service';

interface MarkAsReadSnapshot {
  prevBreakdown: [QueryKey, unknown][];
  prevCount: [QueryKey, unknown][];
}

export const useMarkNotificationAsRead = (
  notification: NotificationResponse,
): UseMutationResult<void, AxiosError, void, MarkAsReadSnapshot> => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { markNotificationsAsRead } = useNotificationsService();

  return useMutation<void, AxiosError, void, MarkAsReadSnapshot>(
    () => markNotificationsAsRead([notification.id]),
    {
      onMutate: async () => {
        await queryClient.cancelQueries({ queryKey: [queryKeys.notificationsCountBreakdown] });
        await queryClient.cancelQueries({ queryKey: [queryKeys.notificationsCount] });

        const severity = (notification.severity ?? 'COMMON').toLowerCase();
        const prevBreakdown = queryClient.getQueriesData({
          queryKey: [queryKeys.notificationsCountBreakdown],
        });
        queryClient.setQueriesData<{ critical: number; important: number; common: number }>(
          { queryKey: [queryKeys.notificationsCountBreakdown] },
          (old) =>
            old
              ? { ...old, [severity]: Math.max(0, (old[severity as keyof typeof old] ?? 0) - 1) }
              : old,
        );

        const prevCount = queryClient.getQueriesData({ queryKey: [queryKeys.notificationsCount] });
        queryClient.setQueriesData<number>({ queryKey: [queryKeys.notificationsCount] }, (old) =>
          old != null ? Math.max(0, old - 1) : old,
        );

        return { prevBreakdown, prevCount };
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.notifications],
          refetchType: 'all',
        });
        await queryClient.invalidateQueries({ queryKey: [queryKeys.notificationsCount] });
        await queryClient.invalidateQueries({ queryKey: [queryKeys.notificationsCountBreakdown] });
      },
      onError: (error: AxiosError, _vars, context) => {
        context?.prevBreakdown.forEach(([key, data]) => queryClient.setQueryData(key, data));
        context?.prevCount.forEach(([key, data]) => queryClient.setQueryData(key, data));
        enqueueSnackbar(getBackendErrorMessage(error), { variant: 'error' });
      },
    },
  );
};
